import {CSSProperties, RefObject, useCallback, useEffect, useMemo, useRef, useState,} from 'react';
import ObserversService from '../../../core/services/observers';
import {deepEqual} from '../../../core/services/utils/utils';
import Sizes from '../../../core/models/static/sizes';
import useIsMounted from '../use-is-mounted';

export type UsePrintPreviewScaleOptions = {
    /** DOM id of the fixed-size page / hidden template used as the design-width source. */
    pageElementId: string;
    /** Horizontal gutter subtracted from the container width before computing scale. */
    gutterPx?: number;
    /**
     * When false, skips observing / measuring (e.g. while the page element is not mounted yet).
     * Flip to true once the page element is expected in the DOM.
     */
    ready?: boolean;
};

export type PrintPreviewScaleState = {
    scale: number;
    dimensionPercentage: number;
};

export type UsePrintPreviewScaleResult = {
    rootRef: RefObject<HTMLDivElement>;
    style: CSSProperties;
    scale: number;
    dimensionPercentage: number;
};

const initialScaling: PrintPreviewScaleState = {
    scale: 1,
    dimensionPercentage: 100,
};

/**
 * Proportionally shrinks a fixed-size print preview to fit the available container width on screen.
 * Sets `--page-scale` and `--page-dimensional-percentage` for screen-only CSS; print media stays true size.
 */
const usePrintPreviewScale = ({
    pageElementId,
    gutterPx = Sizes.defaultPagePadding,
    ready = true,
}: UsePrintPreviewScaleOptions): UsePrintPreviewScaleResult => {
    const rootRef = useRef<HTMLDivElement>(null);
    const isMounted = useIsMounted();
    const isPrintingRef = useRef(false);
    const [scaling, setScaling] = useState<PrintPreviewScaleState>(initialScaling);

    /**
     * Recomputes preview scale from the container width vs the fixed page design width.
     * No-ops when the page element is missing or has zero width, and while the print dialog is open.
     */
    const updateScaling = useCallback((containerWidth: number) => {
        if (!isMounted() || isPrintingRef.current) {
            return;
        }
        const pageWidth = document.getElementById(pageElementId)?.offsetWidth ?? 0;
        if (!pageWidth) {
            return;
        }
        setScaling((prevState) => {
            const scale = Number(Math.min((containerWidth - gutterPx) / pageWidth, 1).toFixed(2));
            const dimensionPercentage = Number(Math.max(100 / scale, 100).toFixed(2));
            const nextState = {scale, dimensionPercentage};
            if (deepEqual(prevState, nextState)) {
                return prevState;
            }
            return nextState;
        });
    }, [gutterPx, isMounted, pageElementId]);

    /**
     * Observes the print view root's parent (and the page element when present) so preview
     * scale tracks container width without affecting print layout.
     */
    useEffect(() => {
        if (!ready) {
            return;
        }
        const container = rootRef.current?.parentElement;
        if (!container) {
            return;
        }

        /**
         * Reads the observed parent container width (not the triggering entry — page element
         * may also be observed so scale recalculates when the fixed page mounts).
         */
        const resizeObserverCallback = () => {
            updateScaling(container.getBoundingClientRect().width);
        };

        // rAF avoids "ResizeObserver loop limit exceeded" (operator ObserversService does not wrap).
        const observer = ObserversService.newResizeObserver(() => {
            requestAnimationFrame(resizeObserverCallback);
        });

        resizeObserverCallback();
        ObserversService.observeResizeObserver(observer, container);

        const pageElement = document.getElementById(pageElementId);
        if (pageElement) {
            ObserversService.observeResizeObserver(observer, pageElement);
        }

        return () => {
            ObserversService.disconnectResizeObserver(observer);
        };
    }, [pageElementId, ready, updateScaling]);

    /**
     * Forces true-size CSS vars before Chrome's print layout so screen preview scale
     * cannot leave content shifted/clipped in the print dialog.
     */
    useEffect(() => {
        /**
         * Resets scale to 1 for the print pipeline.
         */
        const handleBeforePrint = () => {
            isPrintingRef.current = true;
            setScaling(initialScaling);
        };

        /**
         * Restores proportional preview scale after the print dialog closes.
         */
        const handleAfterPrint = () => {
            isPrintingRef.current = false;
            const container = rootRef.current?.parentElement;
            if (container) {
                updateScaling(container.getBoundingClientRect().width);
            }
        };

        window.addEventListener('beforeprint', handleBeforePrint);
        window.addEventListener('afterprint', handleAfterPrint);
        return () => {
            window.removeEventListener('beforeprint', handleBeforePrint);
            window.removeEventListener('afterprint', handleAfterPrint);
        };
    }, [updateScaling]);

    const style = useMemo(() => ({
        '--page-scale': scaling.scale,
        '--page-dimensional-percentage': `${scaling.dimensionPercentage}%`,
    }) as CSSProperties, [scaling.dimensionPercentage, scaling.scale]);

    return {
        rootRef,
        style,
        scale: scaling.scale,
        dimensionPercentage: scaling.dimensionPercentage,
    };
};

export default usePrintPreviewScale;
